// Capas del mapa V2 permitidas por perfil (Parte D del plan de accesos por perfil).
// Con PERMISOS_POR_PERFIL apagado todo se ve como antes; encendido, cada capa
// exige su clave del catálogo de apiv2 (grupo "Capas del mapa").
import { canSeeReportants } from '@/util/user-permissions'

export const CLAVE_POR_CAPA = Object.freeze({
  device: 'capa_device',
  bus: 'capa_bus',
  camera: 'capa_camera',
  lpr: 'capa_lpr',
  route_lpr: 'capa_lpr',
  sensor: 'capa_sensor',
  external_sensor: 'capa_external_sensor',
  external_sensor_route: 'capa_external_sensor',
  incident: 'capa_incident',
  hit: 'capa_hit',
  sismo: 'capa_sismo',
  fire: 'capa_fire',
  presa: 'capa_presa',
  cpr: 'capa_cpr',
  geofence: 'capa_geofence',
  region: 'capa_region',
  sector: 'capa_sector',
  cuadrante: 'capa_cuadrante',
  reportant: 'permission_reportantes',
  sos_call: 'permission_sinaloaSOS',
  medida_cautelar: 'permission_medidas_cautelares',
})

const CAPA_POR_ALERTA = Object.freeze({
  sos_call: 'sos_call',
  incident: 'incident',
  sensor_alert: 'sensor',
  external_sensor_alert: 'external_sensor',
  bus_alert: 'bus',
  device_alert: 'device',
  mc_alert: 'medida_cautelar',
  hit: 'hit',
  reportant: 'reportant',
})

// Categorías de la barra lateral → capas que las alimentan.
const CAPAS_POR_CATEGORIA = Object.freeze({
  911: ['incident'],
  sos: ['sos_call'],
  event: ['device', 'bus', 'sensor', 'external_sensor'],
  hit: ['hit', 'medida_cautelar'],
  reportant: ['reportant'],
})

function esVerdadero(valor) {
  return valor === true || valor === 'true'
}

/** { [entityType]: boolean } para todas las capas del mapa. */
export function capasPermitidas(user) {
  const attrs = (user && user.attributes) || {}
  const porPerfil = !!(user && user.accesosPorPerfil)
  return Object.keys(CLAVE_POR_CAPA).reduce((acc, capa) => {
    if (porPerfil) acc[capa] = esVerdadero(attrs[CLAVE_POR_CAPA[capa]])
    else acc[capa] = capa === 'reportant' ? canSeeReportants(user) : true
    return acc
  }, {})
}

/** Tipos que no son capa (kpi, lugares…) o sin mapa calculado: permitidos. */
export function capaPermitida(permitidas, tipo) {
  if (!permitidas || !(tipo in CLAVE_POR_CAPA)) return true
  return permitidas[tipo] === true
}

export function capaDeAlerta(tipoAlerta) {
  return CAPA_POR_ALERTA[tipoAlerta] || null
}

export function categoriaVisible(categoria, permitidas) {
  const capas = CAPAS_POR_CATEGORIA[categoria]
  if (!capas) return true
  return capas.some((capa) => capaPermitida(permitidas, capa))
}

/**
 * Estado persistido que se rehidrata sin pasar por la ingesta (mismo motivo que
 * purgeReportantState). Función pura: devuelve el estado a aplicar.
 */
export function purgarEstadoDeCapas(estado, permitidas) {
  const permitido = (item) => !item || capaPermitida(permitidas, item.entityType)
  const selectedItem = (estado && estado.selectedItem) || null
  const pinnedItems = (estado && estado.pinnedItems) || []
  const followedUnit = (estado && estado.followedUnit) || null
  return {
    selectedItem: permitido(selectedItem) ? selectedItem : null,
    pinnedItems: pinnedItems.filter(permitido),
    followedUnit: permitido(followedUnit) ? followedUnit : null,
  }
}

/**
 * Ítems de un lote que pueden entrar al mapa: todos si la capa está permitida;
 * si no, solo la llamada SOS activa del propio operador (nunca se filtra).
 * `esPropia(item)` decide si un ítem sos_call es la llamada del operador.
 */
export function filtrarPorCapa(permitidas, tipo, items, esPropia) {
  if (capaPermitida(permitidas, tipo)) return items
  if (tipo === 'sos_call' && typeof esPropia === 'function') return (items || []).filter(esPropia)
  return []
}

/**
 * Visibilidad efectiva de capas: lo guardado por el operador con las capas no
 * permitidas forzadas a false (defensa en profundidad: aunque el localStorage
 * diga true, una capa prohibida nunca se pinta).
 */
export function visibilidadEfectiva(guardada, permitidas) {
  const out = { ...(guardada || {}) }
  Object.keys(CLAVE_POR_CAPA).forEach((tipo) => {
    if (!capaPermitida(permitidas, tipo)) out[tipo] = false
  })
  return out
}

const CAPAS_GEOCERCA = ['geofence', 'region', 'sector', 'cuadrante']

/** Filtros de geocercas guardados AND capas permitidas (solo los 4 niveles). */
export function filtrosGeocercaEfectivos(guardados, permitidas) {
  const out = { ...(guardados || {}) }
  CAPAS_GEOCERCA.forEach((tipo) => {
    out[tipo] = out[tipo] !== false && capaPermitida(permitidas, tipo)
  })
  return out
}
